<template>
  <div class="row">
    <div
      v-for="{ source, types } in sourceMap"
      :key="source.label"
      class="source-toggle q-pa-sm q-ma-xs"
      :class="{ 'text-white': sourceSelected(source) }"
      :style="sourceStyle(source)"
    >
      <div class="column">
        <QCheckbox
          class="text-bold q-pb-xs q-mb-sm media-toggle"
          dense
          :disable="disabled"
          indeterminate-value="some"
          :model-value="sourceEnabled(source)"
          @update:model-value="toggleSource(source, $event)"
        >
          <span class="bigger">{{ source.label }}</span>
        </QCheckbox>
        <QCheckbox
          v-for="(type, typeName) in types"
          :key="type.label"
          v-model="types[typeName].checked"
          class="row"
          dense
          :disable="disabled"
          :val="type.label"
        >
          {{ type.label }}
        </QCheckbox>
      </div>
    </div>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import {
  adminDefaultCheckedContentToggles,
  adminDefaultContentToggles,
  sources,
} from "shared/helpers/media";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";

export default {
  name: "ContentToggles",
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    showAll: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const organisationFeaturesStore = useOrganisationFeaturesStore();

    const {
      defaultContentToggles,
      defaultCheckedContentToggles,
      enabledSources,
    } = storeToRefs(organisationFeaturesStore);

    return {
      defaultContentToggles,
      defaultCheckedContentToggles,
      enabledSources,
    };
  },
  data() {
    return {
      sourceMap: {},
    };
  },
  computed: {
    adminDefaultContentToggles,
    adminDefaultCheckedContentToggles,
    toggles: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    visibleSources() {
      if (this.showAll) return sources.filter((source) => source.filterEnabled);

      return this.enabledSources;
    },
    colors() {
      return this.visibleSources.reduce(
        (acc, source) => ({ ...acc, [source.field]: source.color }),
        {}
      );
    },
    currentDefaultContentToggles() {
      if (this.showAll) return this.adminDefaultContentToggles;

      return this.defaultContentToggles;
    },
    currentDefaultContentCheckedContentToggles() {
      if (this.showAll) return this.adminDefaultCheckedContentToggles;

      return this.defaultCheckedContentToggles;
    },
  },
  watch: {
    sourceMap: {
      deep: true,
      handler: "updateValue",
    },
  },
  mounted() {
    const sourceMap = {};

    this.visibleSources.forEach((source) => {
      const types = {};
      const key = `selected_${source.field}_content`;

      this.enabledTypes(source).forEach((enabledType) => {
        let checked = this.currentDefaultContentCheckedContentToggles[
          key
        ].includes(enabledType.field);

        if (this.modelValue[key]) {
          checked = this.modelValue[key].includes(enabledType.field);
        }

        types[enabledType.field] = {
          label: enabledType.label,
          checked,
        };
      });

      sourceMap[source.field] = {
        source,
        types,
      };
    });

    this.sourceMap = sourceMap;
  },
  methods: {
    enabledTypes(source) {
      if (this.showAll) return source.relatedTypes;

      const key = `selected_${source.field}_content`;

      return source.relatedTypes.filter(({ field }) =>
        this.currentDefaultContentToggles[key].includes(field)
      );
    },
    sourceStyle(source) {
      return {
        "background-color": this.sourceSelected(source)
          ? this.colors[source.field]
          : "#fff",
        "border-color": this.colors[source.field],
      };
    },
    sourceSelected(source) {
      return Object.keys(this.sourceMap[source.field].types).filter(
        (type) => this.sourceMap[source.field].types[type].checked
      ).length;
    },
    sourceEnabled(source) {
      const key = `selected_${source.field}_content`;

      const enabledTypes = this.sourceSelected(source);

      if (!enabledTypes) return false;

      return enabledTypes === this.currentDefaultContentToggles[key].length
        ? true
        : "some";
    },
    toggleSource(source, $event) {
      Object.keys(this.sourceMap[source.field].types).forEach((type) => {
        this.sourceMap[source.field].types[type].checked = $event;
      });
    },
    updateValue() {
      const value = {};

      Object.keys(this.sourceMap).forEach((sourceName) => {
        const key = `selected_${sourceName}_content`;

        const enabledTypes = Object.keys(
          this.sourceMap[sourceName].types
        ).filter((type) => this.sourceMap[sourceName].types[type].checked);

        value[key] = enabledTypes;
      });

      this.toggles = { ...this.toggles, ...value };
    },
  },
};
</script>

<style lang="scss" scoped>
.source-toggle {
  border-width: 2px;
  border-style: solid;
  width: 30%;
}

.media-toggle {
  border-bottom: 1px solid white;
}

.bigger {
  font-size: 16px;
}
</style>
